import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { PublicIntegrationScopeSchema } from './integrations/integration.models.nango_auth';
import { URLParamsSchema } from './models.generic';
import { EmbeddedToolLightSchema } from './models.tools';

extendZodWithOpenApi(z);

/**
 * Schema for email entry data structure used in timesheet reminders and notification metadata
 */
export type EmailEntry = z.infer<typeof EmailEntrySchema>;
export const EmailEntrySchema = z
  .object({
    name: z.string().optional().openapi({
      description: 'Project name (undefined for unassigned entries)',
    }),
    clientName: z.string().optional(),
    durationMinutes: z.number().int(),
    from: z.string().datetime({ offset: true }).optional().openapi({
      description: 'Start datetime with timezone (optional if no time blocks)',
    }),
    to: z.string().datetime({ offset: true }).optional().openapi({
      description: 'End datetime with timezone (optional if no time blocks)',
    }),
    note: z.string().optional(),
    color: z.string(),
    tags: z.array(z.string()).optional(),
    timeFormat: z.enum(['12', '24']).optional().openapi({
      description: 'Preferred time format: 12-hour or 24-hour',
    }),
  })
  .openapi({
    description: 'Email entry data for timesheet reminders',
  });

/**
 *
 * Notification states are used to track the state of a notification for a user.
 *
 */
export const NotificationStateEnums = ['unread', 'read', 'dismissed'] as const;
export const NotificationStateEnumSchema = z.enum(NotificationStateEnums);

export const NotificationStateBaseSchema = z.object({
  id: z.string().uuid().optional(),
  user_id: z.string().uuid(),
  notification_id: z.string().uuid(),
  seen_at: z.string().datetime({ offset: true }).nullable().optional(),
  dismissed_at: z.string().datetime({ offset: true }).nullable().optional(),
});

/**
 * Payload for saving notification state
 */
export type NotificationStateWrite = z.infer<typeof NotificationStateWriteSchema>;
export const NotificationStateWriteSchema = NotificationStateBaseSchema;

export const NotificationStateReadSchema = NotificationStateBaseSchema.required({
  id: true,
  seen_at: true,
  dismissed_at: true,
});

/**
 * What is this notification about? What should the UI do about it?
 * Implement the `NotificationEventEnums` client-side using the embedded information.
 */
const NotificationEventEnums = [
  'user_joined_tic', // PS: userJoinedTic is a thing with Pusher already, consider further consolidation
  'account_provider_connected',
  'personal_provider_connected',
  'provider_sync_completed',
  'tracker_session_flagged_inactive',
  'timesheet_reminder',
  'autosheet_auto_send_paused',
  'provider_authentication_failed',
  'provider_reconnect_required',
] as const;

export type NotificationEvent = z.infer<typeof NotificationEventSchema>;
export const NotificationEventSchema = z.enum(NotificationEventEnums);

const DeliveryMethodEnums = ['email', 'tracker', 'in_app'] as const;
const DeliveryMethodEnumSchema = z.enum(DeliveryMethodEnums);

/** We'll likely need to store/generate some sort of data to pupetteer the UI; this is where we'll store that */
const NotificationMetadataSchema = z
  .object({
    /** Hint to UI on how to display this notification; Add `high` for e.g force popups, and `low` for only a badge? */
    priority: z.enum(['high', 'medium', 'low']).default('medium').optional(),
    /** Auto-set from `getNotifications` based on `account_id` and `user_id` values  */
    audience: z.enum(['global', 'account', 'user']).optional(),
    /** Visual variant instructions for UI */
    variant: z.enum(['default', 'success', 'warning', 'error']).optional(),
    layout: z.enum(['default', 'provider', 'tracker']).default('default').optional(),
    /**
     * Used scope if this notification should lead to opening ProviderManagementModal
     * (e.g, connecting a new provider as individual or account)
     * @see `tool.id` / `tool_id`
     */
    provider_scope: PublicIntegrationScopeSchema.optional(),
    /** @proposed Maybe this is useful? */
    job_queue_id: z.string().optional(),
    /** Timesheet reminder specific metadata */
    date: z.string().optional(),
    entries: z.array(EmailEntrySchema).optional(),
    timesheetLink: z.string().optional(),
    unsubscribeLink: z.string().optional(),
    isUnassignedOnly: z.boolean().optional(),
    totalUnassignedMinutes: z.number().optional(),

    // "Stopped uploading" notification
    deviceHostname: z.string().optional(),
    daysSinceUpload: z.number().optional(),
    showStagingBanner: z.boolean().optional(),

    // "AutoSheet auto-send paused" notification
    /** Days of inactivity that triggered the auto-send pause */
    daysSinceLastActivity: z.number().optional(),
    /** Deep link to the AutoSheet page where the user generates a timesheet to re-arm auto-send */
    autosheetLink: z.string().optional(),
  })
  .nullable();

export const NotificationBaseSchema = z.object({
  id: z.string().uuid().optional(),
  /**
   * md5 of account_id, user_id, tool_id, and event.
   * Helpful to ensure exactly once delivery per "channel",
   * via `eventIsUnique` in `dispatchNotification`.
   */
  conflict_key: z.string().nullable(),
  event: NotificationEventSchema,
  message: z.string(),
  created_at: z.string().datetime({ offset: true }).optional(),
  // deleted_at: z.string().datetime({ offset: true }).nullable(), //
  expired_at: z.string().datetime({ offset: true }).optional().nullable(),
  delivery_methods: z.array(DeliveryMethodEnumSchema).default(['in_app']).optional(),
  metadata: NotificationMetadataSchema.optional(),
  tool_id: z.string().optional().nullable(),
  /** Does this notification belong to one particular user? */
  user_id: z.string().uuid().optional().nullable(),
  account_id: z.string().uuid().nullable(),
});

/** Write notifications */
export type NotificationWrite = z.infer<typeof NotificationWriteSchema>;
export const NotificationWriteSchema = NotificationBaseSchema;

/** Read notifications from DB */
const NotificationReadSchema = NotificationBaseSchema.required({
  id: true,
  account_id: true,
  created_at: true,
  delivery_methods: true,
});

/** Presentable/enriched notification */
export type PresentableNotification = z.infer<typeof PresentableNotificationSchema>;
export const PresentableNotificationSchema = NotificationReadSchema.omit({
  tool_id: true,
  user_id: true,
  conflict_key: true,
  // account_id: true, // need this + tool.id to run /pre-auth if notification is provider related
}).extend({
  tool: EmbeddedToolLightSchema.nullable(),
  state: NotificationStateReadSchema.pick({
    id: true,
    seen_at: true,
    dismissed_at: true,
  }).nullable(),
});

/** Generic API+query params for notifications */
export type NotificationsQuery = z.infer<typeof getNotificationsQuerySchema>;
export const getNotificationsQuerySchema = z.object({
  /** If not supplied, will return the users notifications across all accounts */
  account_id: URLParamsSchema.accountId.optional(),
  user_id: URLParamsSchema.userId.optional(),
  /** Set this to include notifications from personal spaces, _and_ any without an `account_id` */
  personal_account_id: URLParamsSchema.personalAccountId
    .optional()
    .describe(
      'When set, will return personal notifications as well, even in the context of another `account_id`'
    ),
  notification_id: URLParamsSchema.notificationId.optional(),
  page: URLParamsSchema.page.default(1).optional(),
  limit: URLParamsSchema.limit.default(100).optional(),
});
